import { useBloc } from "@blac/react";
import React, { memo } from "react";
import { FC } from "react";
import styled from "@emotion/styled";
import Loader from "../Loader/Loader";
import { keyframes } from "@emotion/react";
import clsx from "clsx";
import { KnownProgram } from "src/state/ProgramBloc/ProgramBloc";
import JourneyModuleTrigger from "./JourneyModuleTrigger";
import UserJourneyBloc from "./UserJourneyBloc";
import Translate from "src/ui/components/Translate/Translate";
import ClockGIF from "src/ui/assets/images/clock.gif";
import translate from "src/lib/translate";
import { JourneyModuleHeader } from "./JourneyModuleHeader";
import { JourneyCard } from "components/UserJourneyOverview/JourneyCard";
import Spacer from "components/UserJourneyOverview/Spacer";

const JourneyCardWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin: 0 -4rem 0;

  @media (max-width: 768px) {
    margin: 0;
  }
`;

const JourneyCardRow = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    justify-content: center;
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const ComingSoon = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-align: center;
  padding-bottom: 9rem;
  position: relative;
`;

const Gradient = styled.div`
  position: absolute;
  background: var(--gradient-mid-morning-360);
  width: 100vw;
  height: 50rem;
  z-index: -1;
`;

const H3 = styled.h3`
  @media (min-width: 605px) {
    width: 30rem;
  }
`;

const Clock = styled.div`
  background-color: white;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  padding: 0.375rem;
`;

const ModuleList = styled.div`
  animation: ${fadeIn} 0.5s ease-in-out;
  h2 {
    text-align: center;
    margin: 0 0 1.2rem 0;
    padding: 0 1.5rem;
    text-wrap: pretty;
    font-size: 2.25rem;

    em {
      display: block;
      text-align: center;
      font-size: 0.875rem;
      font-style: normal;
      font-weight: 600;
      letter-spacing: -0.0175rem;
      text-transform: capitalize;
      font-family: var(--font-family);
      line-height: 2.4;
      color: var(--color-charcoal-80);
    }
  }
`;

const UserJourneyOverview: FC<{ program: KnownProgram }> = memo((props) => {
  const [
    { loading, modules = [], journeyCardsVisible, journeyCardsLocked },
    { onTaskStatusUpdated }
  ] = useBloc(UserJourneyBloc, {
    props: {
      journey: "life-balance-12-week",
      taskProgram: props.program
    }
  });

  if (loading) {
    return <Loader fixed active />;
  }

  return (
    <ModuleList>
      {modules.map((m, mI) => (
        <div
          key={m.name}
          className={clsx({
            "module-list-item": true,
            last: mI === modules.length - 1
          })}
        >
          <JourneyModuleHeader module={m} first={mI === 0} />
          {m.lessons.map((l, lI) => (
            <div
              key={l.name}
              className={clsx({
                "lesson-list-item": true,
                last: lI === m.lessons.length - 1
              })}
            >
              {l.items.map((i) => (
                <JourneyModuleTrigger
                  key={i.type}
                  item={i}
                  lesson={l}
                  module={m}
                  onTaskStatusUpdated={(status) =>
                    onTaskStatusUpdated(i.task, status)
                  }
                />
              ))}
            </div>
          ))}
        </div>
      ))}

      {journeyCardsVisible ? (
        <JourneyCardWrapper>
          <Gradient />

          <Spacer colorScheme="midMorning" />

          <h2>
            <Translate msg="journey.chooseYourNextAdvancedJourney" />
          </h2>

          <JourneyCardRow>
            <JourneyCard
              locked={journeyCardsLocked}
              journey="nutrition"
              colorScheme="goldenHour"
            />
            <JourneyCard
              locked={journeyCardsLocked}
              journey="mindset"
              colorScheme="hazySundown"
            />
            <JourneyCard
              locked={journeyCardsLocked}
              journey="activity"
              colorScheme="fierceSundown"
            />
          </JourneyCardRow>
        </JourneyCardWrapper>
      ) : (
        <ComingSoon>
          <Spacer colorScheme="midMorning" />
          <Gradient />
          <Clock aria-hidden>
            <img src={ClockGIF} alt={translate("clock.alt")} />
          </Clock>
          <H3>
            <Translate msg="lifestyle.comingSoon" />
          </H3>
          <p className="as-subhead">
            <Translate msg="lifestyle.comingSoon.description" />
          </p>
        </ComingSoon>
      )}
    </ModuleList>
  );
});

export default UserJourneyOverview;
